import React from "react";
import { useFormatDateTime } from "@bookingcom/flights-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { HiddenVisually, Text, Badge } from "@bookingcom/bui-react";
import useLocaleContext from "../../../../hooks/useLocaleContext.js";
import getTechnicalStops from "../../../../utils/get-technical-stops.js";
import Frame from "../../Frame/index.js";
import styles from "./FlightCardSegment.mobile.module.css";
import { t } from "@bookingcom/lingojs-core";
import { SPACER_PADDED } from "@flights/constants";
import { formattingUtils } from "@flights/web-api-utils-universal";
const FlightCardSegmentMobile = ({ segment, idx }) => {
    const { departureTime, departureAirport, arrivalTime, arrivalAirport, totalTime, showWarningOriginAirport, showWarningDestinationAirport } = segment;
    const { isRTL } = useLocaleContext();
    if (!departureTime || !departureAirport || !arrivalTime || !arrivalAirport || !totalTime) {
        throw new Error("Invalid flight data in FlightCard");
    }
    return (React.createElement(Frame, null,
        React.createElement(Frame, { direction: "row", justifyContent: "space-between" },
            React.createElement(TimeAndDateBlock, { airport: departureAirport.code, timestamp: departureTime, alignment: isRTL ? "right" : "left", showWarning: showWarningOriginAirport, testAttributes: {
                    time: `flight_card_segment_departure_time_${idx}`,
                    date: `flight_card_segment_departure_date_${idx}`,
                    airport: `flight_card_segment_departure_airport_${idx}`
                } }),
            React.createElement(Stops, { segment: segment, dataTestId: {
                    duration: `flight_card_segment_duration_${idx}`,
                    stops: `flight_card_segment_stops_${idx}`
                } }),
            React.createElement(TimeAndDateBlock, { airport: arrivalAirport.code, timestamp: arrivalTime, alignment: isRTL ? "left" : "right", showWarning: showWarningDestinationAirport, testAttributes: {
                    time: `flight_card_segment_destination_time_${idx}`,
                    date: `flight_card_segment_destination_date_${idx}`,
                    airport: `flight_card_segment_destination_airport_${idx}`
                } }))));
};
const TimeAndDateBlock = ({ timestamp, airport, alignment, showWarning, testAttributes }) => {
    const i18n = useI18n();
    const { formats } = useFormatDateTime(i18n);
    const timeFormatted = formats.flightTime(timestamp);
    const usTimeMatch = timeFormatted.match(/(.+)(AM|PM)$/);
    return (React.createElement("div", { className: styles.timeDateBlock, style: { textAlign: alignment } },
        React.createElement(Text, { variant: "strong_2", attributes: { "data-testid": `${testAttributes?.time}` } }, usTimeMatch?.length === 3 ? (React.createElement(React.Fragment, null,
            usTimeMatch[1],
            React.createElement(Text, { tagName: "span", variant: "small_2" }, usTimeMatch[2]))) : (timeFormatted)),
        React.createElement(Frame, { direction: "row", justifyContent: alignment === "left" ? "flex-start" : "flex-end", className: styles.iataAndDate },
            React.createElement(Text, { variant: "small_1", className: showWarning ? styles.iataWarning : "", attributes: { "data-testid": `${testAttributes?.airport}` } }, airport),
            React.createElement(Text, { variant: "small_1", className: styles.iataDateSeparator }, SPACER_PADDED),
            React.createElement(Text, { variant: "small_1", attributes: { "data-testid": `${testAttributes?.date}` } }, formats.flightDate(timestamp)))));
};
const Stops = ({ segment, dataTestId }) => {
    const { legs, totalTime } = segment;
    const i18n = useI18n();
    const timeInMinutes = totalTime / 60;
    const stopsCount = legs.length - 1;
    const technicalStopsCount = getTechnicalStops.fromSegment(segment).length;
    return (React.createElement(Frame, { className: styles.stopsRoot, alignItems: "center" },
        React.createElement("div", { className: styles.stopsWrap },
            React.createElement("div", { className: styles.stopsConnector }),
            React.createElement(Badge, { text: stopsCount === 0
                    ? i18n.trans(t("flights_filter_stops_none"))
                    : i18n.trans(t("flights_route_num_stops", {
                        num_exception: stopsCount,
                        variables: { num_stops: stopsCount }
                    })), variant: stopsCount === 0 ? "constructive" : "outline", className: styles.stopsBadge, attributes: { "data-testid": dataTestId?.stops } })),
        React.createElement(Text, { variant: "small_2", className: styles.stopsTextStyle, attributes: { "data-testid": dataTestId?.duration, "aria-hidden": "true" } }, formattingUtils.flightDuration(timeInMinutes, i18n)),
        React.createElement(HiddenVisually, null, formattingUtils.flightDurationAria(timeInMinutes, i18n)),
        Boolean(technicalStopsCount) && (React.createElement(Text, { variant: "small_2" }, i18n.trans(t("flights_sr_technical_stop", {
            variables: {
                num_stop: technicalStopsCount,
                num_exception: technicalStopsCount
            }
        }))))));
};
export default FlightCardSegmentMobile;
